/**
 * Keeps browser speech recognition open for a live training block.
 * Each finished phrase is emitted on its own so it can be stamped with
 * the sequence phase at that moment. Existing Pingflo tap-to-talk is unchanged.
 */

const LANG_TAGS = {
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  es: "es-ES",
  nl: "nl-NL",
  pt: "pt-PT",
};

function SpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function createContinuousSpeechSession({
  languageHint = "en",
  onUtterance,
  onState,
} = {}) {
  let rec = null;
  let stopped = true;
  let paused = false;
  let listening = false;
  let emittedFinal = "";

  function setState(state) {
    if (typeof onState === "function") onState(state);
  }

  function emit(text) {
    const clean = String(text || "").replace(/\s+/g, " ").trim();
    if (clean.length < 8) return;
    if (clean === emittedFinal) return;
    emittedFinal = clean;
    if (typeof onUtterance === "function") onUtterance(clean);
  }

  function bind() {
    const Ctor = SpeechRecognitionCtor();
    if (!Ctor || stopped || paused || listening) return;
    const next = new Ctor();
    next.lang = LANG_TAGS[languageHint] || LANG_TAGS.en;
    next.interimResults = true;
    next.continuous = true;
    next.maxAlternatives = 1;
    next.onresult = (event) => {
      const results = event?.results;
      if (!results) return;
      for (let i = event.resultIndex || 0; i < results.length; i += 1) {
        if (!results[i].isFinal) continue;
        emit(results[i][0]?.transcript || "");
      }
    };
    next.onerror = (event) => {
      const name = event?.error || "";
      if (name === "not-allowed" || name === "service-not-allowed") {
        stopped = true;
        setState("denied");
      }
    };
    next.onend = () => {
      if (rec === next) rec = null;
      listening = false;
      if (!stopped && !paused) {
        window.setTimeout(bind, 250);
      } else if (!stopped) {
        setState("paused");
      }
    };
    rec = next;
    try {
      next.start();
      listening = true;
      setState("listening");
    } catch {
      rec = null;
      listening = false;
      if (!stopped && !paused) window.setTimeout(bind, 400);
    }
  }

  return {
    start() {
      stopped = false;
      paused = false;
      emittedFinal = "";
      if (!SpeechRecognitionCtor()) {
        setState("unsupported");
        return;
      }
      bind();
    },
    pause() {
      paused = true;
      listening = false;
      const current = rec;
      rec = null;
      try {
        current?.stop();
      } catch {
        /* already stopped */
      }
      setState("paused");
    },
    resume() {
      if (stopped) return;
      paused = false;
      if (!listening) bind();
    },
    stop() {
      stopped = true;
      paused = false;
      listening = false;
      const current = rec;
      rec = null;
      try {
        current?.abort();
      } catch {
        /* already stopped */
      }
      setState("idle");
    },
  };
}

export function browserSpeechAvailable() {
  return Boolean(SpeechRecognitionCtor());
}
